import Point from "@arcgis/core/geometry/Point";
import { GetCodedValueAndAlias, getMapView, projectToLatLong } from "./arcgisUtils";
import { toast } from "react-toastify";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { saveAs } from "file-saver";
import { formatDate, zoomToObject } from "./helpers";
import { TableCellProperties, TDocumentDefinitions } from "pdfmake/interfaces";
import { logoDataUrl } from "../constants/KyvLogoString";
import { uploadFileToFDV } from "./FDVUtils";
import type Graphic from "@arcgis/core/Graphic";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";

pdfMake.addVirtualFileSystem(pdfFonts);

// Extracted data needed based on existing Skjermingsrapport
interface LightData {
    sjomerkenr: string;
    navn: string;
    sted: string;
    klasse: string;
    beliggenhet: string;
    long: number;
    lat: number;
    byggehoyde: number;
    lyshoyde: number;
    rekkeviddeHvit: number;
    rekkeviddeRod: number;
    rekkeviddeGronn: number;
    lysstyrkeHvit: number;
    lysstyrkeGronn: number;
    lysstyrkeRod: number;
    fyrlisteKarakter: string;
    karakterTid: string;
    bakgrunnsbelysning: string;
    maltAv: string;
    maltDato: Date;
    globalID: string;
}

interface SectorData {
    sektornr: number;
    lysfarge: string;
    sektorTekst: string;
    beskrivelse: string;
    retningsektorlinje1: number;
    retningsektorlinje2: number;
    globalID: string;
}

interface ScreeningData {
    sektorID: number;
    filterForm: string;
    filterKode: string;
    filterBredde: number;
    montertPaa: string;
    fraRetning: number;
    tilRetning: number;
}

export const generateSkjermingsRapport = async (
    lightFeature: Graphic,
    download: boolean,
    upload: boolean,
    workOrderIds?: string[]
) => {
    try {
        const lightData = await formatLightData(lightFeature);
        const sectorData = await fetchAndFormatSectorData(lightData.globalID);
        const screeningData = await fetchAndFormatScreeningData(sectorData);

        const dateString = formatDate(new Date().getTime());
        const mapImageUrl = await generateMapImage(lightFeature);

        const { sectorCsv, screeningCsv } = generateCsvContent(sectorData, screeningData);

        const uploadResults: {
            sectorUploadSuccess: number;
            screeningUploadSuccess: number;
            pdfReportUploadSuccess: number;
        } = {
            sectorUploadSuccess: -1,
            screeningUploadSuccess: -1,
            pdfReportUploadSuccess: -1,
        };

        const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
        if (sectorCsv === "") {
            toast.info("Ingen sektordata å eksportere");
        } else {
            const sectorBlob = new Blob([BOM, sectorCsv], { type: "text/csv;charset=utf-8;" });
            if (download) {
                saveAs(sectorBlob, `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}_sektor.csv`);
            }
            if (upload && workOrderIds) {
                const sectorResult = await uploadFileToFDV(
                    workOrderIds,
                    sectorBlob,
                    `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}_sektor.csv`,
                    "Skjermingsrapport sektordata"
                );
                uploadResults.sectorUploadSuccess = sectorResult ? 1 : 0;
            }
        }

        if (screeningCsv !== "") {
            const screeningBlob = new Blob([BOM, screeningCsv], { type: "text/csv;charset=utf-8;" });
            if (download) {
                saveAs(screeningBlob, `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}_skjerming.csv`);
            }
            if (upload && workOrderIds) {
                const screeningResult = await uploadFileToFDV(
                    workOrderIds,
                    screeningBlob,
                    `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}_skjerming.csv`,
                    "Skjermingsrapport skjermingsdata"
                );
                uploadResults.screeningUploadSuccess = screeningResult ? 1 : 0;
            }
        }

        const pdfBlob = await generatePdfReportNew(lightData, sectorData, screeningData, mapImageUrl);
        if (download) {
            saveAs(pdfBlob, `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}.pdf`);
        }
        if (upload && workOrderIds) {
            const pdfUploadResult = await uploadFileToFDV(
                workOrderIds,
                pdfBlob,
                `Skjermingsrapport_${lightData.sjomerkenr}_${dateString}.pdf`,
                "Skjermingsrapport PDF"
            );
            uploadResults.pdfReportUploadSuccess = pdfUploadResult ? 1 : 0;
        }

        if (upload && workOrderIds) {
            let uploadMessage = "Opplastingsresultat:\n";
            if (uploadResults.sectorUploadSuccess !== -1) {
                uploadMessage += `- Sektordata: ${uploadResults.sectorUploadSuccess === 1 ? "Vellykket" : "Feilet"}\n`;
            }
            if (uploadResults.screeningUploadSuccess !== -1) {
                uploadMessage += `- Skjermingsdata: ${uploadResults.screeningUploadSuccess === 1 ? "Vellykket" : "Feilet"}\n`;
            }
            if (uploadResults.pdfReportUploadSuccess !== -1) {
                uploadMessage += `- PDF-rapport: ${uploadResults.pdfReportUploadSuccess === 1 ? "Vellykket" : "Feilet"}\n`;
            }
            toast.info(uploadMessage);
        }

        toast.success("Skjermingsrapport generert");
    } catch (error) {
        console.error("Error generating Skjermingsrapport:", error);
        toast.error("Feil under generering av skjermingsrapport: " + error);
        return;
    }
};

const formatLightData = async (lightFeature: Graphic): Promise<LightData> => {
    const lightData: Partial<LightData> = {};
    if (!lightFeature.geometry || !(lightFeature.geometry instanceof Point)) {
        throw new Error("Invalid geometry type for light feature");
    }
    const latlongGeometry = (await projectToLatLong(lightFeature.geometry)) as Point;
    if (!latlongGeometry?.latitude || !latlongGeometry?.longitude) {
        throw new Error("Failed to project geometry to lat/long");
    }

    const klasse = GetCodedValueAndAlias(lightFeature.layer, "klasse", lightFeature.attributes.klasse)[1];
    if (!klasse) {
        throw new Error("Failed to get class value for light feature");
    }
    const karakter = GetCodedValueAndAlias(lightFeature.layer, "karakter", lightFeature.attributes.karakter)[1];
    if (!karakter) {
        throw new Error("Failed to get karakter value for light feature");
    }
    const periode = GetCodedValueAndAlias(lightFeature.layer, "periode", lightFeature.attributes.periode)[1];
    if (!periode) {
        throw new Error("Failed to get periode value for light feature");
    }
    const bakgrunnsbelysning = GetCodedValueAndAlias(
        lightFeature.layer,
        "bakgrunnsbelysning",
        lightFeature.attributes.bakgrunnsbelysning
    )[1];
    if (!bakgrunnsbelysning) {
        throw new Error("Failed to get bakgrunnsbelysning value for light feature");
    }

    lightData.sjomerkenr = lightFeature.attributes.sjomerke_nr;
    lightData.navn = lightFeature.attributes.navn;
    lightData.sted = lightFeature.attributes.sted;
    lightData.klasse = klasse?.toString();
    lightData.beliggenhet = lightFeature.attributes.beliggenhet;
    lightData.long = latlongGeometry.longitude;
    lightData.lat = latlongGeometry.latitude;
    lightData.byggehoyde = lightFeature.attributes.byggehoyde;
    lightData.lyshoyde = lightFeature.attributes.lys_hoyde;
    lightData.rekkeviddeHvit = lightFeature.attributes.rekkevidde_hvit;
    lightData.rekkeviddeRod = lightFeature.attributes.rekkevidde_rod;
    lightData.rekkeviddeGronn = lightFeature.attributes.rekkevidde_gronn;
    lightData.lysstyrkeHvit = lightFeature.attributes.lysstyrke_hvit;
    lightData.lysstyrkeGronn = lightFeature.attributes.lysstyrke_gronn;
    lightData.lysstyrkeRod = lightFeature.attributes.lysstyrke_rod;
    lightData.fyrlisteKarakter = karakter?.toString();
    lightData.karakterTid = periode?.toString();
    lightData.bakgrunnsbelysning = bakgrunnsbelysning?.toString();
    lightData.maltAv = lightFeature.attributes.malt_av;
    lightData.maltDato = lightFeature.attributes.malt_dato;
    lightData.globalID = lightFeature.attributes.globalid;

    return lightData as LightData;
};

const fetchAndFormatSectorData = async (lightGlobalId: string): Promise<SectorData[]> => {
    // Fetch sector data based on the light's GlobalID
    const view = getMapView();
    if (!view?.map) return [];
    const sectorTable = view.map.findTableById("Lyssektor") as FeatureLayer;

    if (!sectorTable) {
        throw new Error("Sector table not found");
    }

    const sectorData = await sectorTable
        .queryFeatures({
            where: "FKNavinst = '" + lightGlobalId + "'",
            outFields: ["*"],
        })
        .catch((error) => {
            console.error("Error fetching sector data:", error);
            throw new Error("Failed to fetch sector data");
        });

    if (!sectorData || sectorData.features.length === 0) {
        throw new Error("No sector data found for the given light GlobalID");
    }

    const sectors: SectorData[] = [];
    const sectorColorDictionary: { [key: number]: string } = {};
    sectorColorDictionary[1] = "W";
    sectorColorDictionary[3] = "R";
    sectorColorDictionary[4] = "G";

    for (const feature of sectorData.features) {
        const sector: Partial<SectorData> = {};
        sector.sektornr = feature.attributes.sektor_nr;
        sector.lysfarge = sectorColorDictionary[feature.attributes.lys_farge];
        if (!sector.lysfarge) {
            throw new Error("Invalid lysfarge value for sector: " + feature.attributes.lys_farge);
        }
        sector.sektorTekst = feature.attributes.sektor_tekst;
        sector.beskrivelse = feature.attributes.beskrivelse;
        sector.retningsektorlinje1 = feature.attributes.retning_sektorlinje_1;
        sector.retningsektorlinje2 = feature.attributes.retning_sektorlinje_2;
        sector.globalID = feature.attributes.globalid;
        sectors.push(sector as SectorData);
    }

    return sectors;
};

const fetchAndFormatScreeningData = async (sectors: SectorData[]): Promise<ScreeningData[]> => {
    const view = getMapView();
    if (!view?.map) return [];
    const screeningTable = view.map.findTableById("Skjerming") as FeatureLayer;
    if (!screeningTable) {
        throw new Error("Screening table not found");
    }
    const sektorGlobalIds = sectors.map((sector) => sector.globalID).join("','");
    const screeningData = await screeningTable
        .queryFeatures({
            where: `FKSektorID IN ('${sektorGlobalIds}')`,
            outFields: ["*"],
        })
        .catch((error) => {
            console.error("Error fetching screening data:", error);
            throw new Error("Failed to fetch screening data");
        });

    const screenings: ScreeningData[] = [];
    for (const feature of screeningData.features) {
        const screening: Partial<ScreeningData> = {};
        screening.sektorID = feature.attributes.fksektorid;
        screening.fraRetning = feature.attributes.fra_retning;
        screening.tilRetning = feature.attributes.til_retning;
        const filterForm = GetCodedValueAndAlias(screeningTable, "filter_form", feature.attributes.filter_form)[1];
        screening.filterForm = filterForm?.toString();
        screening.filterKode = feature.attributes.filter_kode;
        screening.filterBredde = feature.attributes.filter_bredde;
        screening.montertPaa = feature.attributes.montert_pa;
        screenings.push(screening as ScreeningData);
    }

    return screenings;
};

const generateMapImage = async (feature: Graphic): Promise<string> => {
    const view = getMapView();
    if (!view) throw new Error("Map view not found");

    const currentExtent = view.extent.clone();

    try {
        await zoomToObject(feature, 8000, undefined, false);
        const sectorRingLayerview = view.allLayerViews.find((lv) => lv.layer.id === "Sektorflate");
        // Wait for the sector ring layer view to finish updating before taking screenshot
        if (sectorRingLayerview) {
            // Create a promise that resolves when updating is false
            await new Promise<void>((resolve) => {
                const checkUpdating = () => {
                    if (sectorRingLayerview.updating) {
                        // If still updating, check again after a small delay
                        setTimeout(checkUpdating, 100);
                    } else {
                        // Updating is complete, resolve the promise
                        resolve();
                    }
                };
                // Start the checking process
                checkUpdating();
            });
        }

        // Now take the screenshot after layer is done updating
        const screenshot = await view.takeScreenshot({
            format: "png",
            width: 920,
            height: 920,
            quality: 100,
        });

        const imageUrl = screenshot.dataUrl;

        return imageUrl;
    } finally {
        view.goTo(currentExtent);
    }
};

const formatDD = (degrees: number): string => {
    const wholeDegrees = Math.floor(degrees);
    const minutes = (degrees - wholeDegrees) * 60;
    const roundedMinutes = Math.round(minutes * 10000) / 10000;
    return `${wholeDegrees}°${roundedMinutes}`;
};

const generateCsvContent = (
    sectorData: SectorData[],
    screeningData: ScreeningData[]
): { sectorCsv: string; screeningCsv: string } => {
    let sectorCsv = "SektorNr,Lysfarge,Sektorområde,Beskrivelse\n";
    let screeningCsv = "SektorNrSkjermKode,Sektorområde,Filterform,Filterbredde(mm)\n";
    const sectorCSVIntialLength = sectorCsv.length;
    const screeningCSVIntialLength = screeningCsv.length;

    const sortedSectors = sectorData.toSorted((a, b) => a.sektornr - b.sektornr);

    for (const sector of sortedSectors) {
        sectorCsv += `${sector.sektornr},${sector.lysfarge},"${sector.retningsektorlinje1.toFixed(1).padStart(5, "0")}° - ${sector.retningsektorlinje2.toFixed(1).padStart(5, "0")}°","${sector.beskrivelse || ""}"\n`;

        let sectorScreenings = screeningData.filter((s) => s.sektorID?.toString() === sector.globalID);
        sectorScreenings = sectorScreenings.sort((a, b) => a.fraRetning - b.fraRetning);

        for (const screening of sectorScreenings) {
            screeningCsv += `${sector.sektornr}${screening.filterKode},"${screening.fraRetning.toFixed(1).padStart(5, "0")}° - ${screening.tilRetning.toFixed(1).padStart(5, "0")}°",${screening.filterForm},${screening.filterBredde}\n`;
        }
    }
    if (sectorCsv.length === sectorCSVIntialLength) {
        sectorCsv = "";
    }
    if (screeningCsv.length === screeningCSVIntialLength) {
        screeningCsv = "";
    }
    return { sectorCsv, screeningCsv };
};

const generatePdfReportNew = async (
    lightData: LightData,
    sectorData: SectorData[],
    screeningData: ScreeningData[],
    mapImageUrl: string
): Promise<Blob> => {
    const navn = lightData.navn;
    const sted = lightData.sted;
    const type = lightData.klasse;
    const beliggenhet = lightData.beliggenhet;
    const datum = "WGS84"; // Check
    const longFormatted = formatDD(lightData.long) + "'E";
    const latFormatted = formatDD(lightData.lat) + "'N";
    const byggeHoyde = lightData.byggehoyde?.toString();
    const lysHoyde = lightData.lyshoyde?.toString();
    const rekkeviddeHvit = lightData.rekkeviddeHvit?.toString();
    const rekkeviddeRod = lightData.rekkeviddeRod?.toString();
    const rekkeviddeGronn = lightData.rekkeviddeGronn?.toString();
    const lysstyrkeHvit = lightData.lysstyrkeHvit?.toString();
    const lysstyrkeGronn = lightData.lysstyrkeGronn?.toString();
    const lysstyrkeRod = lightData.lysstyrkeRod?.toString();
    const fyrlisteKarakter = lightData.fyrlisteKarakter;
    const karakterTid = lightData.karakterTid;
    const bakgrunnsBelysning = lightData.bakgrunnsbelysning;
    const signatur = lightData.maltAv;
    const signDato = lightData.maltDato;
    const sjomerkenr = lightData.sjomerkenr;

    const parsedDate = new Date(signDato);
    const formattedDate = formatDate(parsedDate.getTime());

    const docDefinition: TDocumentDefinitions = {
        content: [],

        styles: {
            header: {
                fontSize: 13,
                bold: false,
                margin: [0, 0, 0, 10],
            },
            subheader: {
                fontSize: 14,
                bold: true,
                margin: [0, 10, 0, 5],
            },
            normal: {
                fontSize: 10,
                margin: [0, 0, 0, 0],
            },
        },
    };

    const pdfContent: TDocumentDefinitions["content"] = [
        {
            table: {
                widths: ["auto", "*", "auto"],
                body: [
                    [
                        { image: logoDataUrl, width: 60, alignment: "left", border: [true, true, false, true] },
                        {
                            text: "SPESIFIKASJON FOR FYRLYKTER, LANTERNER OG BØYER M.V.",
                            style: "header",
                            alignment: "center",
                            margin: [0, 40, 0, 0],
                            border: [false, true, false, true],
                        },
                        { image: logoDataUrl, width: 60, alignment: "right", border: [false, true, true, true] },
                    ],
                ],
            },
        },

        {
            table: {
                widths: ["*", "*", "*"],
                body: [
                    [
                        {
                            style: "normal",
                            text: "Sjømerkenr: " + (sjomerkenr ?? "udefinert"),
                            alignment: "left",
                            border: [true, true, false, false],
                        },
                        {
                            style: "normal",
                            text: "NAVN: " + (navn ?? "udefinert"),
                            alignment: "left",
                            border: [false, true, false, false],
                        },
                        {
                            style: "normal",
                            text: "TYPE: " + (type ?? "udefinert"),
                            alignment: "left",
                            border: [false, true, true, false],
                        },
                    ],
                    [
                        {
                            style: "normal",
                            text: "STED: " + (sted ?? "udefinert"),
                            alignment: "left",
                            border: [true, false, true, false],
                            colSpan: 3,
                        },
                        "",
                        "",
                    ],
                    [
                        {
                            style: "normal",
                            text: "POS: " + latFormatted + " " + longFormatted,
                            alignment: "left",
                            border: [true, false, false, true],
                        },
                        {
                            style: "normal",
                            text: "BELIGGENHET: " + beliggenhet,
                            alignment: "left",
                            border: [false, false, false, true],
                        },
                        {
                            style: "normal",
                            text: "DATUM: " + datum,
                            alignment: "left",
                            border: [false, false, true, true],
                        },
                    ],
                ],
            },
        },
        {
            table: {
                widths: ["*", "auto"],
                body: [
                    [
                        mapImageUrl
                            ? {
                                  image: mapImageUrl,
                                  width: 230,
                              }
                            : [],
                        {
                            table: {
                                widths: ["*", "auto", "auto"],
                                body: [
                                    [
                                        {
                                            text: "KARAKTER: " + fyrlisteKarakter,
                                            style: "normal",
                                            border: [false, false, false, true],
                                        },
                                        { text: karakterTid ?? "udefinert", border: [false, false, false, true] },
                                        { text: "", border: [false, false, false, true] },
                                    ],
                                    [
                                        {
                                            text: "BAKGRUNNSBELYSNING:",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            text: bakgrunnsBelysning ?? "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        { text: "", border: [false, false, false, false] },
                                    ],
                                    [
                                        {
                                            text: "LYSSTYRKE/REKKEVIDDE:",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        { text: "", border: [false, false, false, false] },
                                        { text: "", border: [false, false, false, false] },
                                    ],
                                    [
                                        { text: "HVIT", style: "normal", border: [false, false, false, false] },
                                        {
                                            alignment: "right",
                                            text: lysstyrkeHvit ? `${lysstyrkeHvit} cd` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            alignment: "right",
                                            text: rekkeviddeHvit ? `${rekkeviddeHvit} NM` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                    ],
                                    [
                                        {
                                            alignment: "left",
                                            text: "RØD",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            alignment: "right",
                                            text: lysstyrkeRod ? `${lysstyrkeRod} cd` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            alignment: "right",
                                            text: rekkeviddeRod ? `${rekkeviddeRod} NM` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                    ],
                                    [
                                        {
                                            alignment: "left",
                                            text: "GRØNN",
                                            style: "normal",
                                            border: [false, false, false, true],
                                        },
                                        {
                                            alignment: "right",
                                            text: lysstyrkeGronn ? `${lysstyrkeGronn} cd` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, true],
                                        },
                                        {
                                            alignment: "right",
                                            text: rekkeviddeGronn ? `${rekkeviddeGronn} NM` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, true],
                                        },
                                    ],
                                    [
                                        {
                                            text: "LYSETS HØYDE:",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            alignment: "right",
                                            text: lysHoyde ? `${lysHoyde} m` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        { text: "", border: [false, false, false, false] },
                                    ],
                                    [
                                        {
                                            text: "BYGGETS HØYDE:",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        {
                                            alignment: "right",
                                            text: byggeHoyde ? `${byggeHoyde} m` : "udefinert",
                                            style: "normal",
                                            border: [false, false, false, false],
                                        },
                                        { text: "", border: [false, false, false, false] },
                                    ],
                                ],
                            },
                        },
                    ],
                ],
            },
        },
    ];

    pdfContent.push({ text: "SEKTORER:", style: "subheader", margin: [0, 10, 0, 5] });

    const sectorTable: TDocumentDefinitions["content"] = {
        table: {
            // 70 wide to fit the "000.0° - 000.0°" format on one line.
            widths: ["auto", "auto", 70, "*"],
            body: [],
        },
    };

    const screeningTable: TDocumentDefinitions["content"] = {
        table: {
            widths: ["auto", "auto", "auto", "auto", "*"],
            body: [],
        },
    };

    const sortedSectors = sectorData.toSorted((a, b) => a.sektornr - b.sektornr);

    for (let i = 0; i < sortedSectors.length; i++) {
        const sector = sortedSectors[i];
        const isFirstSector = i === 0;
        const isLastSector = i === sortedSectors.length - 1;

        sectorTable.table.body.push([
            {
                text: sector.sektornr?.toString() + ".",
                style: "normal",
                border: [true, isFirstSector, false, isLastSector],
                alignment: "center",
            },
            {
                text: sector.lysfarge,
                style: "normal",
                border: [false, isFirstSector, false, isLastSector],
                alignment: "center",
            },
            {
                text: `${sector.retningsektorlinje1.toFixed(1).padStart(5, "0")}° - ${sector.retningsektorlinje2.toFixed(1).padStart(5, "0")}°`,
                style: "normal",
                border: [false, isFirstSector, false, isLastSector],
                alignment: "center",
            },
            {
                text: sector.beskrivelse || "",
                style: "normal",
                border: [false, isFirstSector, true, isLastSector],
                alignment: "left",
            },
        ]);

        let sectorScreenings = screeningData.filter((s) => s.sektorID?.toString() === sector.globalID);
        sectorScreenings = sectorScreenings.sort((a, b) => a.fraRetning - b.fraRetning);
        for (const screening of sectorScreenings) {
            const isFirstScreening = screeningTable.table.body.length === 0;
            screeningTable.table.body.push([
                {
                    text: sector.sektornr + screening.filterKode?.toString() + " =",
                    style: "normal",
                    border: [true, isFirstScreening, false, false],
                    alignment: "right",
                },
                {
                    text: `${screening.fraRetning.toFixed(1).padStart(5, "0")}° - ${screening.tilRetning.toFixed(1).padStart(5, "0")}°`,
                    style: "normal",
                    border: [false, isFirstScreening, false, false],
                    alignment: "center",
                },
                {
                    text: screening.filterForm,
                    style: "normal",
                    border: [false, isFirstScreening, false, false],
                    alignment: "left",
                },
                {
                    text: screening.filterBredde + " mm",
                    style: "normal",
                    border: [false, isFirstScreening, false, false],
                    alignment: "right",
                },
                { text: "", border: [false, isFirstScreening, true, false] },
            ]);
        }
    }

    pdfContent.push(sectorTable);

    if (screeningData.length > 0) {
        pdfContent.push({ text: "SKJERMER:", style: "subheader", margin: [0, 10, 0, 5] });
        const screeningBottomRow = screeningTable.table.body[screeningTable.table.body.length - 1];
        for (const cell of screeningBottomRow) {
            const borders = (cell as TableCellProperties).border;
            if (borders?.length !== 4) continue;
            (cell as TableCellProperties).border = [borders[0], borders[1], borders[2], true];
        }
        pdfContent.push(screeningTable);
    }

    pdfContent.push({
        table: {
            widths: ["*", "auto"],
            body: [
                [
                    {
                        text: "SATT I DRIFT / REVIDERT AV : " + signatur,
                        alignment: "left",
                        border: [true, true, false, true],
                    },
                    { text: "DATO: " + formattedDate, alignment: "right", border: [false, true, true, true] },
                ],
            ],
        },
    });

    docDefinition.content = pdfContent;

    return pdfMake.createPdf(docDefinition).getBlob();
};
